import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles,
  ArrowLeft,
  RefreshCw,
  FileText,
  MessageCircle,
  Workflow,
  ClipboardList,
  Loader2,
  Send,
  User,
  Calendar,
  Stethoscope,
  Printer,
  Share2,
} from "lucide-react";
import {
  getConsultation,
  getConsultationFullView,
  analyzeConsultation,
  getConsultationChatHistory,
  sendConsultationChat,
} from "@/lib/adapters/consultations";

const TABS = [
  { id: "intake", label: "Intake", icon: ClipboardList },
  { id: "chat", label: "AI Chat", icon: MessageCircle },
  { id: "report", label: "Clinical Report", icon: FileText },
  { id: "workflow", label: "Workflow & Referral", icon: Workflow },
];

export default function AiConsultantWorkspace() {
  const { caseId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "intake";
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reanalyzing, setReanalyzing] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    Promise.all([
      getConsultation(caseId).catch((e) => { throw e; }),
      getConsultationFullView(caseId).catch(() => null),
    ])
      .then(([base, fullView]) => {
        setRecord(base);
        setFull(fullView);
      })
      .catch((err) => {
        console.error("[workspace] load failed", err);
        setError(err?.response?.status === 401 ? "Please sign in." : "Could not load case.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [caseId]);

  const reanalyze = async () => {
    setReanalyzing(true);
    try {
      await analyzeConsultation(caseId, { force: true });
      toast.success("Re-analysis started");
      setTimeout(load, 1500);
    } catch {
      toast.error("Could not start re-analysis");
    } finally {
      setReanalyzing(false);
    }
  };

  const setTab = (t) => setSearchParams({ tab: t }, { replace: true });

  if (loading) {
    return <main className="dyntra-container py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#4B2AAD]" /></main>;
  }
  if (error) {
    return (
      <main className="dyntra-container py-24 text-center">
        <p className="text-sm text-gray-700">{error}</p>
        <Link to="/ai-consultant" className="text-[#4B2AAD] font-semibold hover:underline">Back to dashboard</Link>
      </main>
    );
  }

  const c = record || {};
  const merged = full || c;

  return (
    <main className="dyntra-container py-8">
      <Link
        to="/ai-consultant"
        data-testid="ws-back"
        className="inline-flex items-center gap-2 text-sm font-semibold text-[#4B2AAD] hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> All cases
      </Link>

      <section className="mt-4 rounded-2xl border border-gray-100 bg-white shadow-sm p-6 flex flex-wrap items-start gap-4 justify-between">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-[#F3EFFC] flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-[#4B2AAD]" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[#4B2AAD]">Case #{c.case_id || c.caseId || c.id}</p>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
              {c.patient_name || c.patientName || c.full_name || c.fullName || "Patient"}
              <RiskBadge risk={c.risk_level || c.riskLevel || c.risk} />
            </h1>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
              <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" /> {c.age || "?"} yrs · {c.gender || "-"}</span>
              <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {(c.created_at || c.createdAt) ? new Date(c.created_at || c.createdAt).toLocaleString() : "—"}</span>
              <span className="inline-flex items-center gap-1"><Stethoscope className="h-3.5 w-3.5" /> {(c.status || c.analysis_status || "pending").toString()}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="ws-reanalyze"
            onClick={reanalyze}
            disabled={reanalyzing}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {reanalyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Re-analyze
          </button>
          <button
            type="button"
            data-testid="ws-view-report"
            onClick={() => navigate(`/ai-consultant/${caseId}/report`)}
            className="dyntra-btn-primary px-4 py-2 text-sm"
          >
            <FileText className="h-4 w-4" /> View report
          </button>
        </div>
      </section>

      <nav className="mt-4 border-b border-gray-100 flex flex-wrap gap-1">
        {TABS.map((t) => {
          const Ico = t.icon;
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              data-testid={`ws-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${active ? "border-[#4B2AAD] text-[#4B2AAD]" : "border-transparent text-gray-500 hover:text-gray-800"}`}
            >
              <Ico className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </nav>

      <section className="mt-6">
        {activeTab === "intake" && <IntakeTab c={merged} />}
        {activeTab === "chat" && <ChatTab caseId={caseId} />}
        {activeTab === "report" && <ReportTab c={merged} caseId={caseId} />}
        {activeTab === "workflow" && <WorkflowTab c={merged} />}
      </section>
    </main>
  );
}

function IntakeTab({ c }) {
  const rows = [
    ["Chief complaint", c.chief_complaint || c.chiefComplaint],
    ["Symptoms", asList(c.symptoms)],
    ["Medical history", c.medical_history || c.medicalHistory],
    ["Dental history", c.dental_history || c.dentalHistory],
    ["Doctor's notes", c.doctor_notes || c.doctorNotes],
    ["Emergency level", c.emergency_level || c.emergencyLevel],
    ["Blood pressure", c.blood_pressure || c.bloodPressure],
    ["Blood group", c.blood_group || c.bloodGroup],
    ["Treatment types", asList(c.dental_problem_types || c.dentalProblemTypes)],
  ].filter(([, v]) => v && v !== "-" && v !== "");

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-6 space-y-5">
      <h2 className="font-display text-lg font-bold text-gray-900">Intake summary</h2>
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt className="text-xs font-bold uppercase tracking-wider text-gray-500">{k}</dt>
            <dd className="text-sm text-gray-900 mt-1 whitespace-pre-line">{String(v)}</dd>
          </div>
        ))}
      </dl>
      {rows.length === 0 && <p className="text-sm text-gray-500">No intake data recorded.</p>}
    </div>
  );
}

function asList(v) {
  if (!v) return "";
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}

function ChatTab({ caseId }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    getConsultationChatHistory(caseId)
      .then((data) => {
        const rows = Array.isArray(data) ? data : (data.messages || data.items || []);
        setMsgs(rows);
      })
      .catch(() => setMsgs([]))
      .finally(() => setLoading(false));
  }, [caseId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [msgs]);

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    setMsgs((prev) => [...prev, { role: "user", content: t, at: new Date().toISOString() }]);
    setText("");
    try {
      const res = await sendConsultationChat(caseId, t);
      const reply = res?.reply || res?.message || res?.response || res;
      const content = typeof reply === "string" ? reply : (reply?.content || reply?.text || "Response received.");
      setMsgs((prev) => [...prev, { role: "assistant", content, at: new Date().toISOString() }]);
    } catch {
      setMsgs((prev) => [...prev, { role: "assistant", content: "Sorry, I couldn't reach the AI. Please try again.", at: new Date().toISOString(), error: true }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-6 flex flex-col min-h-[520px]">
      <h2 className="font-display text-lg font-bold text-gray-900">AI clinical chat</h2>
      <p className="text-xs text-gray-500 mt-1">Ask follow-up questions about this case. Context is preserved automatically.</p>

      <div ref={listRef} className="mt-4 flex-1 overflow-y-auto space-y-3">
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#4B2AAD]" /></div>
        ) : msgs.length === 0 ? (
          <p className="text-sm text-gray-500">No messages yet — ask the AI anything about this case.</p>
        ) : (
          msgs.map((m, i) => {
            const isUser = m.role === "user" || m.sender === "user";
            return (
              <div key={i} className={`flex ${isUser ? "justify-end" : ""}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-line ${isUser ? "bg-[#4B2AAD] text-white" : "bg-[#F3EFFC] text-gray-800"}`}>
                  {m.content || m.text || m.message}
                </div>
              </div>
            );
          })
        )}
        {sending && (
          <div className="flex">
            <div className="max-w-[80%] rounded-2xl px-4 py-2.5 text-sm bg-[#F3EFFC] text-gray-800 inline-flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Dr. Dyntra is thinking…
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 border-t border-gray-100 pt-3 flex items-end gap-2">
        <textarea
          data-testid="ws-chat-input"
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Ask a follow-up…"
          disabled={sending}
          className="flex-1 rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-4 py-2.5 outline-none text-sm resize-none"
        />
        <button
          type="button"
          data-testid="ws-chat-send"
          onClick={send}
          disabled={sending}
          className="dyntra-btn-primary h-11 px-4"
          aria-label="Send"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function ReportTab({ c, caseId }) {
  const report = c.clinical_report || c.ai_report || c.analysis?.report || c.report;
  const summary = c.summary || c.diagnosis || c.assessment;

  if (!report && !summary) {
    return (
      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-8 text-center">
        <FileText className="h-8 w-8 text-gray-300 mx-auto" />
        <p className="mt-2 text-sm text-gray-600">Report is being generated.</p>
        <Link to={`/ai-consultant/${caseId}/report`} className="mt-3 inline-flex text-[#4B2AAD] font-semibold hover:underline text-sm">Open full report →</Link>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-display text-lg font-bold text-gray-900">Clinical report</h2>
        <div className="flex items-center gap-2">
          <Link to={`/ai-consultant/${caseId}/report`} data-testid="ws-open-report" className="dyntra-btn-primary py-2 px-4 text-sm">
            <FileText className="h-4 w-4" /> Open full report
          </Link>
        </div>
      </div>
      <div className="mt-4 prose prose-sm max-w-none whitespace-pre-line text-gray-800">
        {typeof report === "string" ? report : (summary || JSON.stringify(report, null, 2))}
      </div>
    </div>
  );
}

function WorkflowTab({ c }) {
  const referral = c.referral || c.referral_recommendation || c.referralRecommendation;
  const nextSteps = c.next_steps || c.nextSteps || c.recommendations;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-6 space-y-6">
      <div>
        <h2 className="font-display text-lg font-bold text-gray-900">Workflow & referral</h2>
        <p className="text-xs text-gray-500 mt-1">Suggested next steps generated from the AI analysis. Adjust before sharing with the patient.</p>
      </div>

      <Section title="Recommended next steps">
        {Array.isArray(nextSteps) && nextSteps.length ? (
          <ol className="list-decimal ml-5 space-y-1 text-sm text-gray-800">
            {nextSteps.map((s, i) => <li key={i}>{typeof s === "string" ? s : s.step || s.title || JSON.stringify(s)}</li>)}
          </ol>
        ) : nextSteps ? (
          <p className="text-sm text-gray-800 whitespace-pre-line">{String(nextSteps)}</p>
        ) : (
          <p className="text-sm text-gray-500">No workflow suggestions yet.</p>
        )}
      </Section>

      <Section title="Referral guidance">
        {referral ? (
          <p className="text-sm text-gray-800 whitespace-pre-line">{typeof referral === "string" ? referral : JSON.stringify(referral, null, 2)}</p>
        ) : (
          <p className="text-sm text-gray-500">No referral required based on current findings.</p>
        )}
      </Section>

      <Section title="Share">
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
            <Printer className="h-4 w-4" /> Print
          </button>
          <button
            type="button"
            onClick={() => {
              const url = window.location.href;
              navigator.clipboard?.writeText(url).then(() => toast.success("Link copied to clipboard"));
            }}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            <Share2 className="h-4 w-4" /> Copy shareable link
          </button>
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-sm font-bold uppercase tracking-wider text-gray-500">{title}</h3>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function RiskBadge({ risk }) {
  const r = (risk || "").toLowerCase();
  const map = {
    high: { bg: "bg-rose-100", fg: "text-rose-700", label: "High risk" },
    moderate: { bg: "bg-amber-100", fg: "text-amber-700", label: "Moderate" },
    low: { bg: "bg-emerald-100", fg: "text-emerald-700", label: "Low risk" },
  };
  const m = map[r];
  if (!m) return null;
  return <span className={`inline-flex items-center rounded-full ${m.bg} ${m.fg} text-xs font-semibold px-2 py-0.5`}>{m.label}</span>;
}
